'use client';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { MoneyUtils } from '@/shared/utils/money-utils';
import { type CurrenciesType } from '@/shared/schema';
import { useMoney } from '@/backend/firebase/hooks/finances/useMoney';
import { useAuth } from '@/backend/context/auth-management';
import { useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

interface MoneyInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  currency: CurrenciesType;
  onCurrencyChange?: (currency: CurrenciesType) => void;
  error?: boolean;
  disabled?: boolean;
  allowCurrencyChange?: boolean;
  showConversion?: boolean;
  className?: string;
  placeholder?: string;
}

// Single currency mode: only local (BS)
const SINGLE_CURRENCY: CurrenciesType[] = ['BS'];

// Multi-currency mode: BS, USD, EUR
const MULTI_CURRENCIES: CurrenciesType[] = ['USD', 'EUR', 'BS'];

export function MoneyInput({
  value,
  onChange,
  currency = 'USD',
  onCurrencyChange,
  error,
  disabled,
  allowCurrencyChange = true,
  showConversion = false,
  className,
  placeholder = '0.00',
}: MoneyInputProps) {
  const meta = useMemo(() => MoneyUtils.getMetadata(currency), [currency]);
  const money = useMoney();
  const profile = useAuth((s) => s.profile);

  // Determine which currencies to show based on multiCurrency setting
  const isMultiCurrency = profile?.currencyConfig?.multiCurrency ?? true;
  const currencies = isMultiCurrency ? MULTI_CURRENCIES : SINGLE_CURRENCY;

  const conversion = useMemo(() => {
    if (value === undefined || value === null || value <= 0) return null;

    // Always show BS conversion if currency is not BS
    if (currency !== 'BS') {
      return {
        value: money.formatConverted(value, currency, 'BS'),
        label: 'BS',
      };
    }

    // If currency is BS, show USD conversion
    if (currency === 'BS') {
      return {
        value: money.formatConverted(value, 'BS', 'USD'),
        label: 'USD',
      };
    }

    return null;
  }, [value, currency, money]);

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      <div
        className={cn(
          'flex h-16 w-full overflow-hidden rounded-2xl border-2 border-slate-100 bg-white shadow-sm transition-all focus-within:border-slate-900',
          error && 'border-rose-500 bg-rose-50/30 focus-within:border-rose-500',
          disabled && 'cursor-not-allowed bg-slate-50 opacity-50',
        )}
      >
        {/* --- SELECTOR DE MONEDA --- */}
        <div className={cn(
          "group relative h-full border-r border-slate-100 bg-slate-50 transition-colors",
          allowCurrencyChange && !disabled && "hover:bg-slate-100"
        )}>
          {allowCurrencyChange ? (
            <>
              <select
                disabled={disabled}
                value={currency}
                onChange={(e) => onCurrencyChange?.(e.target.value as CurrenciesType)}
                className="absolute inset-0 z-10 h-full w-[80px] cursor-pointer opacity-0 disabled:cursor-not-allowed"
              >
                {currencies.map((c) => {
                  const m = MoneyUtils.getMetadata(c);
                  return (
                    <option key={c} value={c}>
                      {m.flag} {c}
                    </option>
                  );
                })}
              </select>
              <div className="pointer-events-none flex h-full w-[80px] flex-col items-center justify-center gap-0.5">
                <span className="text-xl">{meta.flag}</span>
                <span className="text-[10px] font-black tracking-tighter text-slate-400 uppercase">
                  {currency}
                </span>
              </div>
            </>
          ) : (
            <div className="flex h-full w-[60px] flex-col items-center justify-center gap-0.5">
              <span className="text-xl">{meta.flag}</span>
              <span className="text-[10px] font-black tracking-tighter text-slate-400 uppercase">
                {currency}
              </span>
            </div>
          )}
        </div>

        {/* --- INPUT MASCARIZADO --- */}
        <div className="relative flex flex-1 items-center">
          <div className="pointer-events-none absolute left-3">
            <span className="text-base font-black text-teal-600">
              {meta.symbol}
            </span>
          </div>

          <NumericFormat
            disabled={disabled}
            customInput={Input}
            value={value === undefined ? '' : value}
            onValueChange={(values) => {
              onChange(values.floatValue);
            }}
            thousandSeparator=""
            decimalSeparator="."
            allowedDecimalSeparators={[","]}
            decimalScale={2}
            fixedDecimalScale={false}
            placeholder={placeholder}
            inputMode="decimal"
            className="h-full w-full border-none bg-transparent pr-8 pl-12 text-2xl font-black tracking-tight placeholder:text-slate-200 focus-visible:ring-0"
          />
        </div>
      </div>

      {/* --- CONVERSIÓN EN TIEMPO REAL --- */}
      {showConversion && conversion && (
        <div className="animate-in fade-in slide-in-from-top-1 flex items-center gap-1.5 px-3">
          <div className="h-1 w-1 rounded-full bg-teal-500" />
          <p className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
            Aprox. <span className="text-slate-900">{conversion.value}</span> {conversion.label}
          </p>
        </div>
      )}
    </div>
  );
}
